import React, { useEffect, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import * as styles from './modal.styles';
import Column from '../../../core/components/column/column.component';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import StyleConstants from '../../../core/styles.constants';
import { type RetirementImpact } from '../services/offices.service';

export interface DeletionImpactState {
  readonly impact: RetirementImpact | undefined;
  readonly loading: boolean;
  readonly failed: boolean;
  readonly acknowledged: boolean;
  readonly setAcknowledged: (acknowledged: boolean) => void;
  /** Whether delete may be pressed: the count is in, and acknowledged if it needs to be. */
  readonly confirmable: boolean;
}

/**
 * Loads what a deletion would cancel each time its dialog opens. `load` must be memoised by the caller, or the
 * count is fetched again on every render.
 */
export const useDeletionImpact = (
  showModal: boolean,
  load: (() => Promise<RetirementImpact | undefined>) | undefined,
): DeletionImpactState => {
  const [impact, setImpact] = useState<RetirementImpact | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    // Guards against a late answer landing on a closed dialog, or on one reopened for something else.
    let current = true;

    if (showModal && load != null) {
      setLoading(true);
      setFailed(false);
      setAcknowledged(false);

      load()
        .then((result) => {
          if (!current) return;
          setImpact(result);
          // A failed count is a failure, never zero: "nothing will be cancelled" and "we could not find out" are
          // different sentences, and only one of them makes it safe to press delete.
          setFailed(result === undefined);
        })
        .catch(() => {
          if (current) setFailed(true);
        })
        .finally(() => {
          if (current) setLoading(false);
        });
    }

    return (): void => {
      current = false;
    };
  }, [showModal, load]);

  const hasImpact = impact?.hasImpact ?? false;

  return {
    impact,
    loading,
    failed,
    acknowledged,
    setAcknowledged,
    confirmable: !loading && !failed && impact !== undefined && (!hasImpact || acknowledged),
  };
};

interface Props {
  readonly state: DeletionImpactState;
}

/**
 * States, in numbers, what deleting an office or a company cancels and retires, and asks for an explicit
 * acknowledgement when it destroys anything beyond the office itself - the same shape the room deletion uses.
 */
const DeletionImpactSummary = ({ state }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const { impact, loading, failed, acknowledged, setAcknowledged } = state;

  if (loading) {
    return <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />;
  }

  if (failed || impact === undefined) {
    return <span className={style(styles.error)}>{t('deletion-impact-unavailable')}</span>;
  }

  // Named anything but `count`, so i18next resolves the key as written instead of looking for plural variants.
  const people = impact.people > 0 && (
    <span className={style(styles.bodyText)}>{t('deletion-impact-people', { people: impact.people })}</span>
  );

  if (!impact.hasImpact) {
    return (
      <Column>
        {people}
        <span className={style(styles.bodyText)}>{t('deletion-no-impact')}</span>
      </Column>
    );
  }

  const sharedItems = impact.parkingsUnlinked + impact.facilitiesUnlinked;

  return (
    <Column>
      {people}
      {impact.bookings > 0 && (
        <span className={style(styles.bodyText)}>
          {t('deletion-impact-bookings', { bookings: impact.bookings, days: impact.days })}
        </span>
      )}
      {impact.groups > 0 && (
        <span className={style(styles.bodyText)}>
          {t('deletion-impact-groups', { groups: impact.groups, groupReservations: impact.groupReservations })}
        </span>
      )}
      {impact.facilityReservations > 0 && (
        <span className={style(styles.bodyText)}>
          {t('deletion-impact-facility-reservations', { facilityReservations: impact.facilityReservations })}
        </span>
      )}
      {impact.parkingsRetired > 0 && (
        <span className={style(styles.bodyText)}>
          {t('deletion-impact-parkings-retired', { parkings: impact.parkingsRetired })}
        </span>
      )}
      {impact.facilitiesRetired > 0 && (
        <span className={style(styles.warningText)}>
          {t('deletion-impact-facilities-retired', { facilities: impact.facilitiesRetired })}
        </span>
      )}
      {sharedItems > 0 && (
        <span className={style(styles.bodyText)}>{t('deletion-impact-shared', { items: sharedItems })}</span>
      )}
      <span className={style(styles.bodyText)}>{t('deletion-impact-notified')}</span>
      <span className={style(styles.warningText)}>{t('deletion-cannot-be-undone')}</span>
      <label className={style(styles.bodyText)} htmlFor="deletion-impact-acknowledge">
        <input
          id="deletion-impact-acknowledge"
          type="checkbox"
          checked={acknowledged}
          onChange={(event): void => {
            setAcknowledged(event.target.checked);
          }}
        />
        {t('deletion-impact-acknowledge')}
      </label>
    </Column>
  );
};

export default DeletionImpactSummary;
